import React, { useEffect } from 'react';
import { PagePath } from '../../../navigation/navigation.enums';
import { ChevronIcon, ListItemLayout, ListLayout, PageHeader } from '@keplerco/core';
import { useAppActions, useAppState } from '../../../overmind';
import { GroupAssessmentIcon } from '../../../design/icons/group-assessment.icon';
import { QuestionnaireIcon } from '../../../design/icons/questionnaire.icon';
import { FOPIcon } from '../../../design/icons/fop.icon';
import { PageLoaderLayer } from '../../../components/general/loading-state/loader-layers/page-loader-layer/page-loader-layer';
import KeplerNavlink from '../../../navigation/guards/kepler-navlink';
import { RatingIcon } from '../../../design/icons/rating.icon';
import { FetchType } from '../../../enums';
import { Company } from '../../../models';

export function CreateAssessmentPage(): JSX.Element {
  const { companyVariables } = useAppState();
  const actions = useAppActions();

  const [companyData, setCompanyData] = React.useState<Company>();

  useEffect(() => {
    async function fetchCompanyData() {
      if (!companyVariables.slug) return;
      actions.startLoader({ path: PagePath.assessmentManagementCreate, type: FetchType.PageFetching });
      const data = await actions.getCompany(companyVariables.slug);
      setCompanyData(data);
      actions.stopLoader(PagePath.assessmentManagementCreate);
    }

    fetchCompanyData();
  }, [companyVariables.slug]);

  return (
    <PageLoaderLayer path={PagePath.assessmentManagementCreate}>
      <div className="wrapper stack" style={{ paddingBottom: 30, gap: 30 }}>
        <PageHeader
          breadcrumbs={[
            { name: 'Assessment Management', url: `${PagePath.assessmentManagementBase}${PagePath.assessmentManagement.replace(':companySlug', companyVariables.slug!)}` },
            { name: 'Create assessment', url: `${PagePath.assessmentManagementBase}${PagePath.assessmentManagementCreate.replace(':companySlug', companyVariables.slug!)}` },
          ]}
          title="Create assessment"
          subtitle="Select from Peer Assessment, Questionnaire-Based Assessment, or Fields of Practice Assessment to accurately evaluate skill levels"
          divider
        />

        <ListLayout gap={30}>
          {companyData?.allowRatingAssessment && (
            <ListItemLayout>
              <KeplerNavlink style={{ textDecoration: 'none' }} to={`${PagePath.assessmentManagementBase}${PagePath.assessmentManagementCreateRatingAssessment.replace(':companySlug', companyVariables.slug!)}`} className="card action-card interactive">
                <RatingIcon />

                <div className="action-card-content">
                  <h3>Rating Assessment</h3>
                  <p>Select subjects of an assessment, and the users to rate them on a list of chosen skills</p>
                </div>

                <ChevronIcon tone="primary" strokeWidth={1} size={40} />
              </KeplerNavlink>
            </ListItemLayout>
          )}

          {companyData?.allowGroupAssessment && (
            <ListItemLayout>
              <KeplerNavlink style={{ textDecoration: 'none' }} to={`${PagePath.assessmentManagementBase}${PagePath.assessmentManagementCreateGroupAssessment.replace(':companySlug', companyVariables.slug!)}`} className="card action-card interactive">
                <GroupAssessmentIcon />

                <div className="action-card-content">
                  <h3>Group Assessment</h3>
                  <p>Allow a group of people to endorse each other's skills. Ideal for teams that share similar skill sets, and work in collaboration with one another.</p>
                </div>

                <ChevronIcon tone="primary" strokeWidth={1} size={40} />
              </KeplerNavlink>
            </ListItemLayout>
          )}

          {companyData?.allowQuestionnaireAssessment && (
            <ListItemLayout>
              <KeplerNavlink style={{ textDecoration: 'none' }} to={`${PagePath.assessmentManagementBase}${PagePath.assessmentManagementCreateQuestionnaireAssessment.replace(':companySlug', companyVariables.slug!)}`} className="card action-card interactive">
                <QuestionnaireIcon />

                <div className="action-card-content">
                  <h3>Questionnaire Based Assessment</h3>
                  <p>Answer scenario based questions in a multiple choice format. Ideal for assessing behavioural competencies. Select from a list of assessment questionnaires focused on a variety of essential skill sets.</p>
                </div>

                <ChevronIcon tone="primary" strokeWidth={1} size={40} />
              </KeplerNavlink>
            </ListItemLayout>
          )}

          {companyData?.allowFOPAssessment && (
            <ListItemLayout>
              <KeplerNavlink style={{ textDecoration: 'none' }} to={`${PagePath.assessmentManagementBase}${PagePath.assessmentManagementCreateFOPAssessment.replace(':companySlug', companyVariables.slug!)}`} className="card action-card interactive">
                <FOPIcon />

                <div className="action-card-content">
                  <h3>Fields of Practice Assessment</h3>

                  <p>Allows people to compare their skills against regulatory expectations. Ideal for facilitating CPD assessments that align with regulatory requirements. Ask your customer success manager about adding assessments for new regulatory frameworks.</p>
                </div>

                <ChevronIcon tone="primary" strokeWidth={1} size={40} />
              </KeplerNavlink>
            </ListItemLayout>
          )}
        </ListLayout>
      </div>
    </PageLoaderLayer>
  );
}
